import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Ban, CheckCircle, ChevronLeft, ChevronRight, ArrowUpDown, Award, Plus, X, Sparkles, Star, CheckCircle2, Crown, Shield, Heart, Zap, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { useAdminData } from './AdminContext';
import { formatDateTime, getStatusBadgeClass } from './utils';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { BadgeDisplay, BadgeList } from '../BadgeDisplay';

// Custom Celey Balloon Icon for badge display
const CeleyBalloon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 3.17 2.11 5.85 5 6.71V17c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-1.29c2.89-.86 5-3.54 5-6.71 0-3.87-3.13-7-7-7z" />
    <path d="M11 18h2v1h-2z" />
    <path d="M12 19c-.28 0-.5.22-.5.5v2c0 .28.22.5.5.5s.5-.22.5-.5v-2c0-.28-.22-.5-.5-.5z" />
    <ellipse cx="9" cy="7" rx="1.5" ry="2" fill="white" opacity="0.3" />
  </svg>
);

// Icon map for badge dropdown
const badgeIconMap = {
  'sparkles': Sparkles,
  'star': Star,
  'balloon': CeleyBalloon,
  'check-circle': CheckCircle2,
  'crown': Crown,
  'award': Award,
  'shield': Shield,
  'heart': Heart,
  'zap': Zap
};

// Color map for badge dropdown
const badgeColorMap = {
  'founding': 'text-emerald-600',
  'amber': 'text-amber-500',
  'yellow': 'text-yellow-500',
  'emerald': 'text-emerald-500',
  'blue': 'text-blue-500',
  'purple': 'text-purple-500',
  'rose': 'text-rose-500',
  'gold': 'text-amber-500'
};

const AdminUsersTab = () => {
  const { users, suspendUser, unlockUserAccount, resetUserPassword } = useAdminData();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial search from URL params
  const initialSearch = searchParams.get('search') || '';
  
  // Local state for UI
  const [usersSearch, setUsersSearch] = useState(initialSearch);
  const [usersRoleFilter, setUsersRoleFilter] = useState('all');
  const [usersStatusFilter, setUsersStatusFilter] = useState('all');
  const [usersSortBy, setUsersSortBy] = useState('created_at');
  const [usersSortOrder, setUsersSortOrder] = useState('desc');
  const [usersPage, setUsersPage] = useState(1);
  const [usersPerPage] = useState(25);
  
  // Update search when URL params change
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    if (searchFromUrl && searchFromUrl !== usersSearch) {
      setUsersSearch(searchFromUrl);
      // Clear the search param after applying it
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('search');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams]);
  
  // Modal states
  const [suspendingUser, setSuspendingUser] = useState(null);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [resettingPasswordUser, setResettingPasswordUser] = useState(null);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  const [unlockingUser, setUnlockingUser] = useState(null);
  
  // Badge management states
  const [badgeManagementUser, setBadgeManagementUser] = useState(null);
  const [availableBadges, setAvailableBadges] = useState([]);
  const [userBadges, setUserBadges] = useState([]);
  const [selectedBadgeToGrant, setSelectedBadgeToGrant] = useState('');
  const [badgeNote, setBadgeNote] = useState('');
  const [loadingBadges, setLoadingBadges] = useState(false);
  const [grantingBadge, setGrantingBadge] = useState(false);
  const [revokingBadgeId, setRevokingBadgeId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch available badges on mount
  useEffect(() => {
    fetchAvailableBadges();
  }, []);

  const fetchAvailableBadges = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/badges`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const badges = await response.json();
        setAvailableBadges(badges.filter(b => b.active));
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  };

  const fetchUserBadges = async (userId) => {
    setLoadingBadges(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/badges`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const badges = await response.json();
        setUserBadges(badges);
      }
    } catch (error) {
      console.error('Error fetching user badges:', error);
    } finally {
      setLoadingBadges(false);
    }
  };

  const handleOpenBadgeManagement = (user) => {
    setBadgeManagementUser(user);
    fetchUserBadges(user.id);
    setSelectedBadgeToGrant('');
    setBadgeNote('');
  };

  const handleGrantBadge = async () => {
    if (!selectedBadgeToGrant || !badgeManagementUser) return;
    
    setGrantingBadge(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/users/${badgeManagementUser.id}/badges`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          badge_id: selectedBadgeToGrant,
          note: badgeNote || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Badge granted successfully');
        fetchUserBadges(badgeManagementUser.id);
        setSelectedBadgeToGrant('');
        setBadgeNote('');
      } else {
        toast.error(data.detail || 'Failed to grant badge');
      }
    } catch (error) {
      toast.error('Error granting badge');
      console.error('Error granting badge:', error);
    } finally {
      setGrantingBadge(false);
    }
  };

  const handleRevokeBadge = async (badgeId) => {
    if (!badgeManagementUser) return;
    
    setRevokingBadgeId(badgeId);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/users/${badgeManagementUser.id}/badges/${badgeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note: 'Revoked by admin' })
      });
      
      if (response.ok) {
        toast.success('Badge revoked successfully');
        fetchUserBadges(badgeManagementUser.id);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to revoke badge');
      }
    } catch (error) {
      toast.error('Error revoking badge');
      console.error('Error revoking badge:', error);
    } finally {
      setRevokingBadgeId(null);
    }
  };

  // Get badges that are applicable to this user's role and not already active
  const getGrantableBadges = () => {
    if (!badgeManagementUser) return [];
    
    const userRole = badgeManagementUser.role;
    const activeBadgeIds = userBadges
      .filter(ub => !ub.revoked_at)
      .map(ub => ub.badge_id);
    
    return availableBadges.filter(badge => {
      // Check role compatibility
      const roleMatch = badge.applicable_to === 'both' || 
        (badge.applicable_to === 'provider' && userRole === 'vendor') ||
        (badge.applicable_to === 'customer' && userRole === 'customer');
      
      // Check not already assigned
      const notAssigned = !activeBadgeIds.includes(badge.id);
      
      return roleMatch && notAssigned;
    });
  };

  // Filter, sort, and paginate users
  const filteredUsers = useMemo(() => {
    let filtered = users.filter(u => {
      const matchesSearch = usersSearch === '' || 
        u.name?.toLowerCase().includes(usersSearch.toLowerCase()) ||
        u.email?.toLowerCase().includes(usersSearch.toLowerCase()) ||
        (u.phone && u.phone.includes(usersSearch));
      const matchesRole = usersRoleFilter === 'all' || u.role === usersRoleFilter;
      const matchesStatus = usersStatusFilter === 'all' || (u.status || 'active') === usersStatusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      if (usersSortBy === 'created_at') {
        aVal = new Date(a.created_at);
        bVal = new Date(b.created_at);
      } else if (usersSortBy === 'name') {
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
      }
      if (usersSortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return filtered;
  }, [users, usersSearch, usersRoleFilter, usersStatusFilter, usersSortBy, usersSortOrder]);

  const totalUsersPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginatedUsers = filteredUsers.slice(
    (usersPage - 1) * usersPerPage,
    usersPage * usersPerPage
  );

  const handleSuspend = async () => {
    await suspendUser(suspendingUser.id, true, suspensionReason);
    setSuspendingUser(null);
    setSuspensionReason('');
  };

  const handleUnsuspend = async (userId) => {
    await suspendUser(userId, false);
  };

  const handleUnlock = async (email) => {
    setUnlockingUser(email);
    await unlockUserAccount(email);
    setUnlockingUser(null);
  };

  const handleResetPassword = async () => {
    const email = typeof resettingPasswordUser === 'object' ? resettingPasswordUser.email : resettingPasswordUser;
    const success = await resetUserPassword(email, newUserPassword);
    if (success) {
      setResettingPasswordUser(null);
      setNewUserPassword('');
    }
  };

  const handleImpersonate = async (targetUser) => {
    if (targetUser.is_admin) {
      toast.error('Cannot impersonate another admin');
      return;
    }
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('session_token');
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${API_URL}/api/admin/impersonate/${targetUser.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json();
        toast.error(err.detail || 'Failed to impersonate');
        return;
      }
      const data = await response.json();
      // Save admin state for restoration
      localStorage.setItem('impersonation', JSON.stringify({
        adminToken: token,
        adminUser: currentUser,
        targetUserId: targetUser.id,
        targetName: targetUser.name,
        targetEmail: targetUser.email,
      }));
      // Switch auth context in-memory (no full page reload)
      login(data.user, data.token);
      localStorage.setItem('activeRole', data.user.role || 'customer');
      const dest = data.user.role === 'vendor' ? '/vendor/dashboard' : '/customer-dashboard';
      navigate(dest);
    } catch (err) {
      toast.error('Impersonation failed');
    }
  };

  // Get active badges count for a user (to show in table)
  const getActiveBadgesForUser = (userId) => {
    // This would need to be fetched, for now just show the badge button
    return null;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>Manage user accounts, suspensions, and badges</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={usersSearch}
                  onChange={(e) => { setUsersSearch(e.target.value); setUsersPage(1); }}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={usersRoleFilter} onValueChange={(v) => { setUsersRoleFilter(v); setUsersPage(1); }}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="vendor">Vendor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Select value={usersStatusFilter} onValueChange={(v) => { setUsersStatusFilter(v); setUsersPage(1); }}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Users Table */}
          <div className="border rounded-lg overflow-x-auto max-w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50" 
                    onClick={() => {
                      if (usersSortBy === 'name') {
                        setUsersSortOrder(usersSortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setUsersSortBy('name');
                        setUsersSortOrder('asc');
                      }
                    }}
                  >
                    <div className="flex items-center">
                      Name
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      if (usersSortBy === 'created_at') {
                        setUsersSortOrder(usersSortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setUsersSortBy('created_at');
                        setUsersSortOrder('desc');
                      }
                    }}
                  >
                    <div className="flex items-center">
                      Created
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((userItem) => (
                    <TableRow key={userItem.id}>
                      <TableCell className="font-medium">
                        {userItem.name}
                        {userItem.suspension_reason && (
                          <p className="text-xs text-red-600 mt-1">{userItem.suspension_reason}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-600">{userItem.email}</TableCell>
                      <TableCell className="text-gray-600">{userItem.phone || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{userItem.role}</Badge>
                        {userItem.is_admin && (
                          <Badge className="ml-2 bg-purple-100 text-purple-800">Admin</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeClass(userItem.status || 'active', 'user')}>
                          {(userItem.status || 'active').charAt(0).toUpperCase() + (userItem.status || 'active').slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDateTime(userItem.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end flex-wrap">
                          {/* Act As Button */}
                          {!userItem.is_admin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleImpersonate(userItem)}
                              className="border-blue-300 text-blue-700 hover:bg-blue-50"
                              data-testid={`impersonate-${userItem.id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Act As
                            </Button>
                          )}
                          {/* Badge Management Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenBadgeManagement(userItem)}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            <Award className="h-4 w-4 mr-1" />
                            Badges
                          </Button>
                          
                          {!userItem.is_admin && (
                            (userItem.status || 'active') !== 'suspended' ? (
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => setSuspendingUser(userItem)}
                              >
                                <Ban className="h-4 w-4 mr-1" />
                                Suspend
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleUnsuspend(userItem.id)}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Unsuspend
                              </Button>
                            )
                          )}
                          {!userItem.is_admin && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnlock(userItem.email)}
                                disabled={unlockingUser === userItem.email}
                              >
                                {unlockingUser === userItem.email ? 'Unlocking...' : 'Unlock'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setResettingPasswordUser(userItem)}
                              >
                                Reset Pwd
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalUsersPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-600">
                Showing {((usersPage - 1) * usersPerPage) + 1} to {Math.min(usersPage * usersPerPage, filteredUsers.length)} of {filteredUsers.length} users
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                  disabled={usersPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  Page {usersPage} of {totalUsersPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUsersPage(p => Math.min(totalUsersPages, p + 1))}
                  disabled={usersPage === totalUsersPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Badge Management Dialog */}
      <Dialog open={!!badgeManagementUser} onOpenChange={() => setBadgeManagementUser(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-600" />
              Manage Badges
            </DialogTitle>
            <DialogDescription>
              Grant or revoke badges for {badgeManagementUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          {badgeManagementUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold">
                    {badgeManagementUser.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium">{badgeManagementUser.name}</p>
                    <p className="text-sm text-gray-500">{badgeManagementUser.email}</p>
                  </div>
                  <Badge variant="outline" className="ml-auto capitalize">{badgeManagementUser.role}</Badge>
                </div>
              </div>

              {/* Current Active Badges */}
              <div>
                <Label className="text-sm font-medium">Current Badges</Label>
                {loadingBadges ? (
                  <p className="text-sm text-gray-500 mt-2">Loading badges...</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {userBadges.filter(ub => !ub.revoked_at).length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No badges assigned</p>
                    ) : (
                      userBadges.filter(ub => !ub.revoked_at).map(ub => (
                        <div key={ub.id} className="flex items-center justify-between bg-white border rounded-lg p-2">
                          <div className="flex items-center gap-2">
                            {ub.badge && <BadgeDisplay badge={ub.badge} size="sm" />}
                            <span className="text-xs text-gray-500">
                              Granted {new Date(ub.granted_at).toLocaleDateString()}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRevokeBadge(ub.badge_id)}
                            disabled={revokingBadgeId === ub.badge_id}
                          >
                            {revokingBadgeId === ub.badge_id ? (
                              'Revoking...'
                            ) : (
                              <>
                                <X className="h-3 w-3 mr-1" />
                                Revoke
                              </>
                            )}
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Grant New Badge */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium">Grant New Badge</Label>
                <div className="mt-2 space-y-3">
                  <Select value={selectedBadgeToGrant} onValueChange={setSelectedBadgeToGrant}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a badge to grant..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getGrantableBadges().length === 0 ? (
                        <SelectItem value="_none" disabled>No badges available for this user</SelectItem>
                      ) : (
                        getGrantableBadges().map(badge => {
                          const IconComponent = badgeIconMap[badge.icon] || Sparkles;
                          const iconColor = badgeColorMap[badge.color] || 'text-gray-500';
                          return (
                            <SelectItem key={badge.id} value={badge.id}>
                              <div className="flex items-center gap-2">
                                <IconComponent className={`h-4 w-4 ${iconColor}`} />
                                {badge.label}
                                <span className="text-xs text-gray-400">({badge.applicable_to})</span>
                              </div>
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                  
                  <div>
                    <Label className="text-xs text-gray-500">Note (optional)</Label>
                    <Input
                      placeholder="Reason for granting this badge..."
                      value={badgeNote}
                      onChange={(e) => setBadgeNote(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  
                  <Button
                    onClick={handleGrantBadge}
                    disabled={!selectedBadgeToGrant || grantingBadge}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                  >
                    {grantingBadge ? (
                      'Granting...'
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Grant Badge
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Badge History */}
              {userBadges.filter(ub => ub.revoked_at).length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium text-gray-500">Revoked Badges (History)</Label>
                  <div className="mt-2 space-y-1">
                    {userBadges.filter(ub => ub.revoked_at).map(ub => (
                      <div key={ub.id} className="flex items-center gap-2 text-sm text-gray-400">
                        <span className="line-through">{ub.badge?.label || 'Unknown Badge'}</span>
                        <span className="text-xs">
                          (Revoked {new Date(ub.revoked_at).toLocaleDateString()})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspension Dialog */}
      {suspendingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Suspend User</CardTitle>
              <CardDescription>Review user details before suspension</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 p-4 rounded space-y-2">
                <div>
                  <Label className="text-xs text-blue-600">User Details</Label>
                  <p className="font-semibold text-lg">{suspendingUser.name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-gray-500">Email</Label>
                    <p className="text-gray-700">{suspendingUser.email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Phone</Label>
                    <p className="text-gray-700">{suspendingUser.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Role</Label>
                    <p className="text-gray-700 capitalize">{suspendingUser.role}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Status</Label>
                    <p className="text-gray-700 capitalize">{suspendingUser.status || 'active'}</p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="suspension-reason">Suspension Reason (Internal)</Label>
                <Textarea
                  id="suspension-reason"
                  value={suspensionReason}
                  onChange={(e) => setSuspensionReason(e.target.value)}
                  placeholder="Enter reason for suspension..."
                  rows={3}
                  className="mt-2"
                />
              </div>

              <div className="bg-yellow-50 p-3 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This user will not be able to log in or perform any actions.
                  {suspendingUser.role === 'vendor' && ' All service provider listings will be hidden from public search.'}
                </p>
              </div>

              <div className="flex space-x-2">
                <Button
                  variant="destructive"
                  onClick={handleSuspend}
                  className="flex-1"
                >
                  <Ban className="h-4 w-4 mr-1" />
                  Confirm Suspension
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setSuspendingUser(null); setSuspensionReason(''); }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset Password Dialog */}
      <Dialog open={!!resettingPasswordUser} onOpenChange={() => { setResettingPasswordUser(null); setNewUserPassword(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
            <DialogDescription>Review user details before resetting password</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {resettingPasswordUser && (
              <div className="bg-blue-50 p-4 rounded space-y-2">
                <div>
                  <Label className="text-xs text-blue-600">User Details</Label>
                  <p className="font-semibold text-lg">{resettingPasswordUser.name || resettingPasswordUser}</p>
                </div>
                {typeof resettingPasswordUser === 'object' && (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-xs text-gray-500">Email</Label>
                      <p className="text-gray-700">{resettingPasswordUser.email}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Phone</Label>
                      <p className="text-gray-700">{resettingPasswordUser.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Role</Label>
                      <p className="text-gray-700 capitalize">{resettingPasswordUser.role}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="newUserPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newUserPassword"
                  type={showNewUserPassword ? 'text' : 'password'}
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  placeholder="Enter new password (min 10 characters)"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewUserPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showNewUserPassword ? 'Hide password' : 'Show password'}
                  data-testid="toggle-new-password-visibility"
                >
                  {showNewUserPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Password must be at least 10 characters with 3+ character types</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => { setResettingPasswordUser(null); setNewUserPassword(''); }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={!newUserPassword || newUserPassword.length < 10}
              >
                Reset Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminUsersTab;
